/*
 * The in-app guide, as DATA.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ THIS IS NOT HELPER TEXT, AND IT IS NOT AN EXCEPTION CARVED OUT OF THE RULE.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This codebase carries a standing prohibition — *"Do not add helper text to the
 * UI. Ever."* No `hint` on a field, no `description` on a `Modal` or a `Panel`,
 * no explanatory sentence under a control, no instruction dressed as a
 * placeholder. That rule is right and it still binds everywhere else.
 *
 * **It forbids prose ATTACHED TO A CONTROL**, where the sentence competes with
 * the control for the reader's attention, is read by people who did not want it,
 * and rots silently against the thing it sits beside. **This file is a
 * DESTINATION** — a drawer somebody opens on purpose, reads, and closes. Nothing
 * here renders next to a field. Removing it removes a page, not a caption.
 *
 * ⚠️ **So a helper-text sweep must not delete this file**, and the next person
 * running one is right to have stopped here to check. The test the rule itself
 * supplies — *what wrong conclusion does this prevent?* — is answered by the
 * whole document rather than sentence by sentence: it prevents a new joiner
 * guessing at a CRM whose vocabulary is not obvious from its screens.
 *
 * ⚠️ **The rule DOES still bind inside the drawer.** `GuideDrawer` has one
 * `Input` with a placeholder and section headers that are labels. Being a guide
 * is not a licence to explain the guide.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ EVERY SENTENCE HERE IS A CLAIM THAT NOTHING TYPECHECKS.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A wrong type fails the build. A wrong sentence ships, renders perfectly, and
 * is believed — by exactly the person who has no other way to check it. Three
 * working rules, learned the hard way elsewhere in this repo:
 *
 * 1. **Verify against `src/` before writing, not from memory.** Every claim
 *    below was read out of the page or module that implements it.
 * 2. **Never assert the ABSENCE of something** — *"there is no way to…"*,
 *    *"X is not available"*. Those rot the moment somebody builds the thing,
 *    and the builder never re-reads the guide. **Name the control that DOES
 *    exist instead**; a positive claim fails visibly.
 * 3. **Do not print a COUNT** of anything a person can add — fields, stages,
 *    modules, sections. Counts here have moved twice in a week and nothing
 *    fails when one goes stale.
 *
 * Every user-facing string writes **SEED EMERGE**, never "Emerge".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ IT IS WRITTEN FOR SOMEBODY WHO DOES NOT WORK ON THIS CODEBASE.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Owner's instruction, 2026-09-04: the guide is read by people who use the CRM,
 * not by people who build it. So the vocabulary here is the business's own.
 *
 * ⛔ **No role SLUGS.** A person reads `Operations`, `University Partnerships`,
 * `Accounts`, `Management`, `Super Admin` — `ROLE_LABELS` in `@/lib/roles` is
 * the one source of those words, and `basic_user` means nothing to anybody.
 * ⛔ **No endpoint names, no status codes, no field names, no "board" called a
 * kanban, no "the API".** Say what a person does and what they will see.
 *
 * ⚠️ **Say which ACCESS LEVELS have a capability, by their label**, wherever it
 * depends on one. That is the whole point of the section on access levels: a
 * reader on Operations should be able to tell what is theirs without asking.
 */

import { NAV_SURFACE_MODULE, type Ability, type ModuleSlug } from '@/lib/permissions';

/** One piece of a section's body. `note` is the warning treatment. */
export type GuideBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: readonly string[] }
  | { kind: 'note'; tone: 'warning' | 'info'; text: string };

export interface GuideSection {
  /**
   * Stable, and stable is the point: it is the React key, the disclosure's
   * identity across a filter, and what `guideSectionIdForPath` returns. Renaming
   * one is harmless; reusing one is not.
   */
  id: string;
  title: string;
  /**
   * Route prefixes this section is about, so the drawer opens on the section
   * covering the page behind it.
   *
   * Matched as an exact path or a path followed by `/`, so `/quotes` covers
   * `/quotes/27` without also claiming `/quote-something-else`. **Longest match
   * wins** — see `guideSectionIdForPath`.
   */
  routes?: readonly string[];
  /**
   * Areas this section is about, so a reader is never taught a page their
   * access level does not offer them. **ANY of them is enough** — see
   * `isGuideSectionOffered`, which carries the reasoning for the union.
   *
   * ⚠️ **The three gate fields deliberately MIRROR `NavItemBase` in
   * `@/lib/nav`** — `module`, `adminOnly`, `superAdminOnly` — so that whoever
   * gates a nav row recognises the shape here. They are not a second COPY of
   * the rule: `isGuideSectionOffered` asks `ability.canRead` / `ability.isAdmin`
   * / `ability.isSuperAdmin`, which are the same three questions
   * `isNavItemOffered` asks, answered by the one implementation in
   * `@/lib/permissions`.
   *
   * ⚠️ **It DEGRADES OPEN, because `ability.canRead` does**: only an explicit
   * `Denied` hides a section. An older API, an unseeded set or a permission
   * fetch that has not landed leaves the whole guide readable. A guide that
   * hid itself while permissions loaded would read as broken, which is the
   * same call the sidebar makes.
   */
  modules?: readonly ModuleSlug[];
  /** Hide below the Management tier — for pages that tier alone may open. */
  adminOnly?: boolean;
  /** Hide below Super Admin. */
  superAdminOnly?: boolean;
  body: readonly GuideBlock[];
}

/*
 * ⚠️ Content as DATA rather than JSX, deliberately, and it buys three things
 * that a component full of `<p>` tags cannot:
 *
 *  - the drawer can FILTER it, because there is a string to match against;
 *  - a second surface could render the same sections without a second copy of
 *    the sentences, which is how two screens start disagreeing;
 *  - the sentences sit in one file that a reviewer can read end to end, rather
 *    than being spread through markup.
 */
export const GUIDE_SECTIONS: readonly GuideSection[] = [
  /*
   * ⚠️ **NO GATE, DELIBERATELY — this section and two others are what stop the
   * guide ever opening EMPTY.** See `isGuideSectionOffered`. Do not add a
   * `modules` list here without checking that at least one section is still
   * ungated: a drawer with nothing in it reads as an app that is broken.
   */
  {
    id: 'getting-around',
    title: 'Getting around',
    body: [
      {
        kind: 'paragraph',
        text: 'The sidebar groups pages under Dashboards, Trackers, Modules and Admin, with Modules Catalog as a row beneath them. Open as many groups as you like — what you leave open is remembered.',
      },
      {
        kind: 'paragraph',
        text: 'A row marked Soon is an area that is not built yet. When it ships, that same row gains its link rather than a second row appearing beside it.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'You are shown the rows your access level allows. If a colleague describes a page you cannot find in the sidebar, that is what to ask about — see Your access level below.',
      },
      { kind: 'heading', text: 'Search' },
      {
        kind: 'paragraph',
        text: 'Search SEED EMERGE from the top bar, or press Ctrl-K — Command-K on a Mac — from anywhere. It looks through companies, contacts, deals, products and the staff list, and it jumps straight to a page by name. Records start arriving once you have typed two characters, and the pages it offers are the ones your access level allows.',
      },
      { kind: 'heading', text: 'Filters live in the address bar' },
      {
        kind: 'paragraph',
        text: 'On the list pages, what you filter, sort and page to is written into the address. So a filtered list is a link: copy the address out of the bar and whoever opens it sees the same rows. Back, reload and bookmarking all work the way you would expect.',
      },
      { kind: 'heading', text: 'Table or board' },
      {
        kind: 'paragraph',
        text: 'Companies, deals, quotes and invoices each offer a board of columns you can drag a card between. The table is what opens by default, and the board is the opt-in.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'The table opens first because every per-record action lives on the row — tasks and notes, edit, delete, and the menu that creates the next document. Switch to the board to move things along; come back to the table to work on one record.',
      },
      { kind: 'heading', text: 'Where you land' },
      {
        kind: 'paragraph',
        text: 'Signing in takes you to the page SEED EMERGE opens for you, and typing the bare address takes you to the same place. That is the University Partnerships Dashboard unless your access level names another one.',
      },
    ],
  },

  /*
   * ⚠️ **NO `routes`, DELIBERATELY.** The layout manager sits on most pages, so
   * any route listed here would either tie with the page's own section (and
   * lose, being later) or, on a page nothing else claims, become the section
   * the drawer opens on — hiding the page's real explanation. It is found by
   * search instead. Ungated, because every access level can arrange a page.
   */
  {
    id: 'arranging-a-page',
    title: 'Arranging a page',
    body: [
      {
        kind: 'paragraph',
        text: 'Pages made of several sections — the dashboards, the record pages, Enquiries, Product Insights and Settings — can be arranged to suit you. Press Edit layout in the page header to start.',
      },
      {
        kind: 'list',
        items: [
          'Move a section by dragging its grip, or with Move up and Move down.',
          'Full or Half sets its width. Two half-width sections sit side by side on a wide screen, and stack on a narrow one.',
          'Hide takes a section off the page. It goes to the Hidden sections tray, and Show brings it back.',
          'Reset returns the page to its built-in layout.',
          'Done finishes arranging.',
        ],
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Your layout is saved to your account, so it follows you to any device you sign in on. It changes the page for you alone — nobody else sees it.',
      },
      {
        kind: 'paragraph',
        text: 'On a company page, folding a section open or closed works as it always has.',
      },
    ],
  },

  /*
   * ⚠️ **THE SECTION THE OWNER ASKED FOR, AND THE SECOND UNGATED ONE.** Every
   * other section can be hidden from somebody; this one explains WHY, so it is
   * exactly the section a reader who has just met a refusal comes looking for.
   * Gating it would hide the explanation from the people who need it.
   */
  {
    id: 'access-level',
    title: 'Your access level',
    body: [
      {
        kind: 'paragraph',
        text: 'Everybody has one access level. It decides what you can open and what you can change, and it is shown beside your name on the Users page.',
      },
      {
        kind: 'list',
        items: [
          'Operations — read what is in SEED EMERGE. Creating, editing and deleting belong to the other levels.',
          'University Partnerships — everything Operations reads, plus creating and editing records: companies, contacts, deals, quotes, invoices, projects, and moving cards on a board.',
          'Accounts — the same access as University Partnerships today.',
          'Management — the above, plus deleting records, managing people, editing the reference lists, and Settings and the audit log.',
          'Super Admin — everything Management has, plus editing the access levels themselves.',
        ],
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'AN ACCESS LEVEL IS NOT A TEAM. Accounts does not limit anything to the accounts team — it is full access to the records, including delete. Somebody who works in accounts may hold any of these levels.',
      },
      { kind: 'heading', text: 'When something is not available to you' },
      {
        kind: 'paragraph',
        text: 'A control you may not use is shown disabled with the reason beside it rather than hidden, so nothing reads as broken. A whole page your level does not offer says so on the page, and names what you can still reach from the sidebar.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'YOUR ACCESS LEVEL DECIDES WHAT SEED EMERGE SHOWS YOU. It tidies the app down to the job in front of you. Read it that way, and never as a reason a record is safe from being read.',
      },
      {
        kind: 'paragraph',
        text: 'Each level is a permission set of the same name, so your access level and your permission set are one thing rather than two. A change to a set reaches you the next time you sign in.',
      },
    ],
  },

  /*
   * ⚠️ **`/invoice-tracker` IS GONE and this section used to name it.** The
   * Invoice Tracker was retired on 2026-09-04; the Future Billables Tracker
   * replaced it, and has a section of its own below. A stale route in a
   * `routes` list is silent — the drawer simply never opens on that page — so
   * check every one against `App.tsx` when you touch this file.
   */
  {
    id: 'dashboards',
    title: 'Dashboards',
    routes: ['/university-partnerships', '/management', '/accounts-dashboard'],
    modules: [
      NAV_SURFACE_MODULE.dashboardUniversityPartnerships,
      NAV_SURFACE_MODULE.dashboardManagement,
      NAV_SURFACE_MODULE.dashboardAccounts,
    ],
    body: [
      {
        kind: 'paragraph',
        text: 'A dashboard reads the same records the Modules pages hold. They are different questions asked of one set of data, not separate copies of it.',
      },
      {
        kind: 'list',
        items: [
          'University Partnerships Dashboard — your own accounts: leads, deals, quotes, invoices and companies, each scoped to the account lead, with your targets beside them. My region shows every company in the billing regions that name you as their default account lead.',
          'Management Dashboard — the revenue reading of the business: Financial Analytics, Revenue by Company, Revenue by Product, Top Movers and Sales targets.',
          'Accounts Dashboard — the finance reading of quotes and invoices, plus work somebody has planned and nobody has raised yet, in three sections of one page: Quotes, Invoices and Future Billables.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Account lead, at the top of the University Partnerships Dashboard, chooses whose accounts it shows, and starts on you. Only Super Admin can pick another account lead, Unassigned or All, unless the permission set grants University Partnerships Dashboard - Account Lead Switcher. The Leads, Deals, Quotes, Invoices and Companies tables each carry an Account lead column. The figures above them follow the chosen account lead when one is picked, and show the whole organisation otherwise.',
      },
      {
        kind: 'paragraph',
        text: 'Follow-ups due, on the University Partnerships Dashboard, shows three counts of your own Prospect companies: Overdue, Due soon and Resurfacing. Press one to open the Prospect Follow-ups Tracker showing only yours in that bucket.',
      },
      { kind: 'heading', text: 'Reading the figures' },
      {
        kind: 'note',
        tone: 'warning',
        text: 'A DASH IS NOT A ZERO. Where a target figure is shown, a dash means it could not be worked out, or is not yours to read. A real figure may genuinely be zero, and it is printed as one.',
      },
      {
        kind: 'paragraph',
        text: 'Every money figure on the target tiles is excluding VAT. Booked and Quoted are the quotes’ totals before tax. Invoiced is the invoices’ totals before tax, less approved credit notes, each taken off on the credit note’s own date, so a period with more credited than billed can read below zero. Collected is the cash paid against invoices, with the VAT share taken out. Pipeline is deal value, and deals carry no tax.',
      },
      {
        kind: 'paragraph',
        text: 'Target figures are shown in the currency chosen above them. On Targets & performance and on Financial Analytics, Rates chooses how a document in another currency is converted, and starts on Period end. Both open in the Reporting currency on your account; a currency or Rates you pick there is remembered and used from then on. Document date converts each document at the published reference rate for its own date. Period end converts every document to GBP at its own date and then into the chosen currency at the rate for the end of the period (or today, if the period has not ended), the way Xero’s profit and loss report does. Either way a total is a fair restatement rather than the figure the accounts will show. Where a document has no stored rate for its date it is left out, and the number left out is printed beneath the figures.',
      },
      { kind: 'heading', text: 'The documents behind a figure' },
      {
        kind: 'paragraph',
        text: 'On the University Partnerships Dashboard and on Management → Financial Analytics, click a tile’s figure to open the documents behind it: the same period, currency, Rates and account lead or region the tile was worked out with. Each row is one document, with its amount as counted in that figure and the projects on its lines. On Financial Analytics the Invoiced and Regional invoiced figures in Targets & commission, and every member’s figure in Contribution by member, open their documents the same way.',
      },
      {
        kind: 'list',
        items: [
          'Every column has a filter, and Columns chooses which columns are shown.',
          'Export to Excel downloads every row you can open, with every column, whatever the filters and the column choice on screen.',
          'Under Invoiced, approved credit notes are listed as their own rows with a negative amount, on the credit note’s own date, so the rows add up to the figure on the tile.',
          'Records you cannot open are not listed. When the figure includes some, a line beneath the table says how many rows there are and what they come to, so the total still reconciles.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Commission is worked out on revenue excluding VAT. Credit notes are not taken off that revenue: they reduce pay as clawbacks, also measured excluding VAT. Commission always converts at each document’s own date, whatever Rates is set to.',
      },
      {
        kind: 'paragraph',
        text: 'Under Invoiced, on the University Partnerships Dashboard, three bars show how far you are against your commission targets: Personal target, Regional target and My contribution to region, each as a percentage. They come from your commission assignment for the selected cycle, always cover the whole cycle whatever Report by says, and are in your scheme’s currency rather than the one chosen above. Click them to see the exact figures and a split by billing region. They do not appear when you have no commission assignment for that cycle.',
      },
      {
        kind: 'paragraph',
        text: 'Each commission scheme sets its own new business gate: the same gate as the scheme, no gate, the personal target, the regional target or both targets, with its own gate percentages and gate shape where set, and the scheme’s own where not. A statement records the new business gate it was worked out with and the share of new business commission that gate allowed.',
      },
      {
        kind: 'paragraph',
        text: 'On the Accounts Dashboard the cards at the top are counts and never money — the documents behind them are in several currencies, so one total would be wrong. A card reading Not available means the figure could not be fetched, which is a different thing from a count of nothing. Click a card to filter the section beneath it, and click it again to clear that filter.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'Add future billables records work that has been agreed. It creates no quote and no invoice. Turning a planned row into a real document is a separate action on the row itself, in the Future Billables section.',
      },
      {
        kind: 'paragraph',
        text: 'The section, the view and the filters are all in the address bar on these pages too, so a particular reading of a dashboard is a link you can send to somebody.',
      },
      { kind: 'heading', text: 'Revenue by Company' },
      {
        kind: 'paragraph',
        text: 'Expand a company to see what it has bought. Table lays its revenue out as products down the side and cycles across the top; Lines lists the invoice lines behind it.',
      },
      {
        kind: 'list',
        items: [
          'Each row is a product. A line brought in from Xero without a product from the catalogue is grouped under the line text it carries in Xero.',
          'Each column is a cycle, 1 April to 31 March, starting from the first cycle in which the company paid anything.',
          'Each cell is cash collected including tax, in the currency chosen at the top of the page. Darker shading marks a bigger figure.',
          'Rows and columns each end in a total. The share column is each product’s part of the company’s total, and the change figure compares a cycle with the one before it.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Hover over a cell to light up its row and column. Click a cell to keep them lit, and click it again or press Esc in the table to let go. Click a column header to sort by it. Export CSV downloads the table.',
      },
      { kind: 'heading', text: 'Revenue by Product' },
      {
        kind: 'paragraph',
        text: 'Expand a product to see who bought it. Table lays its revenue out as companies down the side and cycles across the top; Breakdown splits it by cycle and region of focus.',
      },
      {
        kind: 'list',
        items: [
          'Each row is a company. Click its name to open the company.',
          'Each cell is cash collected including tax, in the currency chosen at the top of the page. Darker shading marks a bigger figure.',
          'Rows and columns each end in a total. The share column is each company’s part of the product’s total, and the change figure compares a cycle with the one before it.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Hovering, clicking, Esc, sorting and Export CSV work as they do in Revenue by Company.',
      },
      /*
       * Sales targets (owner 2026-10-28). Gated by its own permission module,
       * `sales-targets`: Management and Super Admin by default. Every rule
       * below is the backend's `sales-targets.math.ts` / `.achieved.ts` /
       * `.regions.ts` and the two-way sheet in `sales-targets-sheet.*` — re-read
       * them before changing a sentence.
       */
      { kind: 'heading', text: 'Sales targets' },
      {
        kind: 'paragraph',
        text: 'Sales targets, on the Management Dashboard, sets out the plan for one cycle project by project, and how far the business has got against it. Choose the cycle at the top. It opens for Management and Super Admin; another access level sees it when its permission set grants Sales targets. Changing a figure needs the edit permission as well.',
      },
      {
        kind: 'list',
        items: [
          'Every figure on this tab is in USD.',
          'Planned revenue and Target revenue are the planned and target counts multiplied by the project’s card rate. Institutions and partners are counted separately and earn at the same card rate, and both are added into the revenue.',
          'The card rate is the project’s USD price in the price book, at the price tier chosen for the cycle (Early bird unless changed): its flat price, or its per-city rate times the project’s cities. A unit price typed on the row replaces it. A project with no price shows a dash for its revenue, not a zero.',
          'For a tour, planned is the planned capacity and target is the target count. For every other project the target is the planned count unless a target is typed.',
          'Where a travel percentage is set, that share of the institutions and partners also buys the project’s travel package, at its own Early bird USD price, in both the planned and the target revenue.',
          'Where months are set, revenue is the months times the unit price.',
          'Achieved is what has been invoiced on the project’s lines, excluding VAT, less approved credit notes on those lines, converted to USD at the rate for the end of the cycle (or today, if the cycle has not ended).',
          'Behind is the target revenue less what has been achieved. Pace compares achieved with where the target says you should be by today, measured through the cycle, so a positive pace gap means behind pace.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'A pool groups projects that share one plan — Masterclasses, counted in places at one unit price, and DAC, set as one revenue figure. Each project in a pool still shows its own achieved; the pool’s row carries the plan and the target, and they are counted once in the totals.',
      },
      {
        kind: 'paragraph',
        text: 'The regional chart sets each billing-region team’s target against what that team has invoiced, the same invoiced figure the targets tiles use, alongside where the team should be by today. Targets set in another currency are converted to USD at the rate for the end of the cycle.',
      },
      { kind: 'heading', text: 'The Sales targets sheet' },
      {
        kind: 'paragraph',
        text: 'Sales targets is copied to a Google Sheet, one tab per cycle named after it, refreshed about every ten minutes. The planned and target counts, travel percentage, months, unit price and notes on a project row, and the plan on a pool row, can be changed on the sheet, and the change is brought back into SEED EMERGE. Every other column is worked out and is rewritten each time.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'SEED EMERGE WINS. When the same figure has been changed both in SEED EMERGE and on the sheet since the last copy, the SEED EMERGE value is kept, the sheet cell is put back, and the row’s Sync notes say why.',
      },
    ],
  },

  {
    id: 'companies-contacts',
    title: 'Companies and contacts',
    routes: ['/companies', '/contacts'],
    modules: ['companies', 'contacts'],
    body: [
      {
        kind: 'paragraph',
        text: 'A company is a school or a faculty — the account you bill. Contacts are the people at it. Because schools within one university bill separately, they are deliberately kept as separate companies rather than merged into the parent.',
      },
      { kind: 'heading', text: 'Company name and billing name' },
      {
        kind: 'paragraph',
        text: 'A company has two names. The Company name is what SEED EMERGE shows everywhere — lists, dashboards, trackers, deals, tasks, emails and search. The Billing name is the company’s name in Xero, and it is the name quotes, invoices and credit notes show for their customer, in SEED EMERGE, on the documents themselves and in DocuSign.',
      },
      {
        kind: 'list',
        items: [
          'Both names are required. When you create a company, the Billing name fills in from the Company name as you type, until you change it yourself.',
          'Editing the Billing name updates the company in Xero. Editing the Company name never does.',
          'Two companies may share a Company name; SEED EMERGE warns you when a name looks like one already there. The Billing name must be unique, because Xero bills under it.',
          'Both names are edited in the company form and on the company page, appear as columns on the companies list and in the Companies Google Sheet, and both find the company in search.',
        ],
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'A Billing name that matches another company’s Billing name is refused, and so is a blank one.',
      },
      { kind: 'heading', text: 'Approval' },
      {
        kind: 'paragraph',
        text: 'A company carries an approval status — pending, approved or rejected. The approvers receive a link by email and decide there; they do not need to be signed in to SEED EMERGE. Approving is also what creates the account in Xero.',
      },
      {
        kind: 'paragraph',
        text: 'If a pending company has been waiting and nobody was told, Resend for approval on the company raises the round again. There is a cooldown, so a second press soon after the first is refused with the time it will next be allowed.',
      },
      { kind: 'heading', text: 'Stage, account lead and client success manager' },
      {
        kind: 'paragraph',
        text: 'The stage — Lead, Prospect, Client, Past Client — is where the account has reached, and it is a different question from whether the company was approved. Stage, account lead and client success manager are all edited straight on the list row, by University Partnerships and above.',
      },
      {
        kind: 'paragraph',
        text: 'A stage status narrows the stage down one level, for example where a Prospect has got to. Each stage has its own statuses, set up in Modules Catalog. Changing the stage clears the stage status, so pick a new one for the new stage.',
      },
      { kind: 'heading', text: 'Account lead history' },
      {
        kind: 'paragraph',
        text: 'SEED EMERGE keeps a history of who has been each company’s account lead, and from when to when. The Account lead history section of a company page lists it, with the current lead marked Current. When you change a company’s account lead, SEED EMERGE asks for the date the change takes effect from: today unless you pick an earlier date, and never a date in the future.',
      },
      {
        kind: 'list',
        items: [
          'Anyone who can see the company can read its history. Management can add, change and remove periods; periods may leave gaps but never overlap.',
          'Management can also load the history for many companies at once from a spreadsheet, with Account lead history on Modules Catalog. A preview shows each company’s history before and after, and nothing is saved until you apply it.',
          'Targets and commissions credit each document to the lead on the document’s own date — the bill date for an invoice, the issue date for a quote, the closing date for a deal, and the day it was paid for collected cash — unless the document names its own account lead. Where that person is not the account lead the page shows, the deal, quote or invoice page shows Credited to beside the account lead.',
          'Everything else, such as who can see a company, who is notified and the dashboards, follows today’s lead.',
        ],
      },
      { kind: 'heading', text: 'Stage status columns' },
      {
        kind: 'paragraph',
        text: 'Stage statuses appear as one column per stage, named after it — Prospect status, Client status and so on — on the companies list, on the Leads and Companies tables of the University Partnerships Dashboard, and in the Companies Google Sheet. A stage’s column appears on its own once that stage has statuses in Modules Catalog. A table that holds only some stages shows only those stages’ columns.',
      },
      {
        kind: 'list',
        items: [
          'Only the column for the company’s current stage can be changed, and it offers only that stage’s statuses. The other stages’ columns stay blank for that company.',
          'When the company moves to another stage, its status clears and the column you edit moves with it.',
          'Each column has a filter listing that stage’s statuses. Choices made in several columns combine, so ticking a Prospect status and a Client status shows the companies holding either.',
          'Each column sorts by its statuses in the order they are arranged in Modules Catalog. In either direction, companies in that stage with no status come after them, and companies in another stage come last.',
          'The company page labels the status with its stage, the board card shows it, and the export has one column per stage.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'A Prospect’s stage status is its sub-stage, and a sub-stage can do more than label the company: it can set how soon the company must be contacted, ask for a follow-up date, or ask for a reason. Where one asks for something, a window opens when you pick it and the change is saved once you confirm. See Prospect sub-stages and follow-ups for what each one does.',
      },
      { kind: 'heading', text: 'Emails on a company' },
      {
        kind: 'paragraph',
        text: 'The Emails section of a company page lists the email conversations logged against the company, newest first, with who wrote last and how many messages each holds. Open one to see its email trail. See Logging emails for how a conversation gets there.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'Where a company has a short name, the list shows it on a second line UNDER the Company name, never instead of it. Two schools at one university read almost identically, and the tail of the name is the part that tells them apart.',
      },
      {
        kind: 'paragraph',
        text: 'Contacts are indexed on Modules Catalog, and reached in practice from the company they belong to. A contact carries a department, edited on the company that holds them.',
      },
      {
        kind: 'paragraph',
        text: 'The people button on a company row — on the companies list and on the University Partnerships Dashboard — shows how many contacts the company has and opens them in one window. There you can edit a contact straight in the row, add one, make one the primary contact, change their department, or delete them; pressing an email address opens a new message in your email app. What you may change follows your access level.',
      },
      {
        kind: 'paragraph',
        text: 'A company page has a Tasks section listing every task on the company and on its deals, quotes and invoices, in three tabs: Outstanding (due today or earlier, or undated), Upcoming, and Previous (done or cancelled).',
      },
      {
        kind: 'paragraph',
        text: 'Below it, a Notes section lists every note on the company and on its deals, quotes and invoices, newest first, with search, a filter by record, and a System notes switch that also shows notes SEED EMERGE wrote itself. A note added there is filed on the company.',
      },
      { kind: 'heading', text: 'Product Insights on a company' },
      {
        kind: 'paragraph',
        text: 'The Product Insights section of a company page shows figures from Funnel Analytics & BI, Events, Lead & Application Engagement, In-Country Representation, AI Visa Tutor, Challenge Hub and School Profiles for the Funnel Analytics & BI schools and SEED platform records linked to the company, one block per product with its headline figures. It appears once somebody links a school on Funnel Analytics & BI → School links or a platform record in Company mapping, and each block’s title opens that product’s Product Insights page.',
      },
      { kind: 'heading', text: 'Company mapping' },
      {
        kind: 'paragraph',
        text: 'The Company mapping section of a company page lists the SEED platform schools, universities and partners linked to the company. Link SEED platform record finds one to add, and Unlink removes one. Several platform records can belong to the same company. The links are the same ones the Company mapper on Modules Catalog makes, and the Product Insights figures for the company include every record linked here.',
      },
      { kind: 'heading', text: 'The Companies Google Sheet' },
      {
        kind: 'paragraph',
        text: 'The companies list also appears as a Google Sheet, with dropdowns for the fields that take a choice from a list and a filter row on the headings. Edits made in the sheet sync back to SEED EMERGE every 10 minutes.',
      },
      {
        kind: 'paragraph',
        text: 'Each stage with statuses has its own dropdown column in the sheet, listing only that stage’s statuses. An edit in a stage’s column is saved only when the company is in that stage; otherwise it is refused and the Sync notes column says why. Statuses that ask for a follow-up date or a reason are set in SEED EMERGE rather than in the sheet.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'Where the same company was changed in both places, SEED EMERGE wins and the sheet is rewritten to match. An edit SEED EMERGE cannot accept is refused, and the Sync notes column says why. When too many rows change at once, the whole sheet is held, nothing on it is saved, and super admins are told.',
      },
    ],
  },

  {
    id: 'money-path',
    title: 'Deals, quotes and invoices',
    routes: ['/deals', '/quotes', '/invoices'],
    /*
     * ⚠️ **ANY of the three, not all three — and this is the section the
     * decision was taken on.** A set may grant quotes and deny invoices, and
     * the honest options were a section-level union or a gate on every block.
     * The union wins because these three are ONE story: a quote is billed into
     * an invoice, so a per-block gate leaves a reader with prose that stops in
     * the middle of the sentence that matters. See `isGuideSectionOffered`.
     */
    modules: ['deals', 'quotes', 'invoices'],
    body: [
      {
        kind: 'paragraph',
        text: 'Work moves along one path: a deal is what is being negotiated, a quote is the offer, an invoice is the demand for money. Each carries line items, and the line items are where the detail lives. Reading them is open to every access level; creating and editing them is University Partnerships and above, and deleting one is Management and above.',
      },
      { kind: 'heading', text: 'Line items' },
      {
        kind: 'paragraph',
        text: 'A line names a product, a quantity, a price, a discount and — on quotes and invoices — a tax rate. The amount is worked out for you and is not something to type.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'The project is chosen PER LINE, not on the document. A project names exactly one product and a document sells several, so the project, the cities and the regions all sit inside the line, stacked under the product picker.',
      },
      {
        kind: 'paragraph',
        text: 'The same product can appear twice on one document when the two lines are under different projects. That is allowed on purpose.',
      },
      {
        kind: 'paragraph',
        text: 'A discount can be entered either as a cash amount or as a percentage — the toggle beside the box says which you are typing, and the row reads back what it actually takes off.',
      },
      { kind: 'heading', text: 'How a line’s price fills' },
      {
        kind: 'paragraph',
        text: 'Price, directly under the project on every deal, quote and invoice line, picks the price tier the line is sold at: Standard, Exclusive or Early bird. A new line starts on Standard.',
      },
      {
        kind: 'paragraph',
        text: 'The unit price comes from the project’s Pricing, in the document’s currency and the line’s tier. A product priced per city is the per-city rate times the cities picked, unless the project fixes a price for exactly that many cities. A product with a flat price takes it, unless the project fixes a price for that quantity.',
      },
      {
        kind: 'list',
        items: [
          'A tier the project has no price for uses its Standard price.',
          'Where the project has no price for the product in that currency, the line uses the product’s catalogue price for all of the project’s cities, divided by the project’s cities, times the cities picked. A product at 17,500 on a project with 5 cities is 3,500 a city, so 2 cities is 7,000.',
          'A line you have not typed a price into reprices when you change its project, tier, cities or quantity.',
          'A price you type yourself is kept.',
          'Turning a deal into a quote or an invoice, or billing a quote, copies each line’s tier onto the new document.',
        ],
      },
      { kind: 'heading', text: 'Line notes' },
      {
        kind: 'paragraph',
        text: 'Add note, under a line, keeps a short internal note on it — "MBA only", "will decide on travel later". It is separate from the line’s description, which is what the customer reads.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'A line note is INTERNAL. It stays in SEED EMERGE: it is never sent to Xero and never appears on a PDF the customer receives.',
      },
      {
        kind: 'list',
        items: [
          'Turning a deal into a quote or an invoice, or billing a quote, copies each line’s note onto the new document. From then on each document’s note is its own.',
          'The note can also be edited on the convert screen itself, before the new document is created.',
          'A note can be edited at any time, even on a quote that has been sent or an invoice that is awaiting payment or paid. Changing only the note sends nothing to Xero and leaves the amounts alone.',
        ],
      },
      { kind: 'heading', text: 'How a deal’s stage moves' },
      {
        kind: 'list',
        items: [
          'Creating a quote from a deal, or linking a quote to one, moves the deal to the quote-sent stage — unless it is already further along, won or lost.',
          'When a quote linked to a deal is accepted or signed, or an accepted quote is linked to a deal, the deal moves to the Quote Signed stage — unless it is already further along, won or lost.',
          'Raising an invoice straight from a deal moves the deal to the won stage, unless it is already won or lost.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'A stage never moves backwards this way, the deal’s line items are left as they were, and a system note on the deal records each move.',
      },
      {
        kind: 'paragraph',
        text: 'Quote signed, on the deals list, the deal page, the University Partnerships dashboard, the deals board and the Sales Tracker, shows the deal’s quote that is currently Accepted, Partially Accepted or Invoiced, with the date it was first accepted, and links to that quote. Where several are, it shows the most recently accepted. A quote that moves back off those stages stops counting, though the deal’s stage stays where it is.',
      },
      { kind: 'heading', text: 'Previewing a quote’s PDF' },
      {
        kind: 'paragraph',
        text: 'Preview PDF opens the quote’s PDF in a window inside SEED EMERGE — the same PDF Xero produces for the customer. It is offered at every stage, from Draft through to Declined and Expired, and wherever a quote is listed: on the quote itself, on the quotes list, on a deal’s quotes, on the deals list, on the send-for-signature screen, on the University Partnerships and Accounts dashboards, and in a company’s sales and billing history. Any access level that can read quotes can preview one.',
      },
      {
        kind: 'list',
        items: [
          'On the deals list, the preview button on a deal with one quote opens that quote. A deal with several quotes offers a short list of them, newest first, each with its number, stage and total, to pick from.',
          'Turning a deal into a quote, and New quote, both offer Create and preview beside the usual create button. It creates the Draft quote exactly as the other button does, sends it to Xero as a draft, and opens its PDF.',
          'A quote that has not reached Xero has no PDF yet. The preview window says so and offers Send to Xero and preview, which sends the quote and then shows its PDF. If Xero refuses it, the window shows Xero’s reason instead. Send to Xero and preview is for the access levels that may edit quotes, University Partnerships and above.',
        ],
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'A PDF can be OUT OF DATE. When the quote has been changed in SEED EMERGE since it last reached Xero, the preview window carries one line saying the changes made since then are not in this PDF. Saving the quote again is what sends the changes to Xero.',
      },
      {
        kind: 'paragraph',
        text: 'The preview window carries the next steps, each shown only to the access levels allowed to take it:',
      },
      {
        kind: 'list',
        items: [
          'Download saves the PDF being shown. It follows the right to export quotes, which every access level that reads quotes has unless its permission set says otherwise.',
          'Mark Sent asks you to confirm, then marks the quote Sent in Xero and in SEED EMERGE and shows the PDF again. It is for University Partnerships and above.',
          'Send for signature opens the DocuSign send screen for the quote, for the same access levels as Mark Sent.',
          'Open quote goes to the quote’s own page. It is left off when you are already on that page.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Marking a quote Sent leaves it open to change. Edits saved afterwards still reach the quote in Xero, until the customer accepts or declines it or it expires.',
      },
      { kind: 'heading', text: 'Accepting a quote' },
      {
        kind: 'note',
        tone: 'warning',
        text: 'Accepting a quote MOVES ITS STAGE AND DOES NOTHING ELSE. It raises no invoice. Billing is a separate step you take afterwards, on the quote itself.',
      },
      { kind: 'heading', text: 'Billing a quote' },
      {
        kind: 'list',
        items: [
          'Convert to invoice opens a New invoice form already filled in from the quote. Nothing is created until you save it, and saving takes you to the new invoice.',
          'Bill line items, on the quote, raises an invoice for whole lines you pick — the way to bill a quote in stages, across several invoices.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'A quote that bills in stages refuses Convert to invoice and says so, and points you at Bill line items instead.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Money is never converted between currencies. Every figure is shown with its own currency code, and a list of documents in mixed currencies deliberately shows no single total, because one would be wrong.',
      },
    ],
  },

  {
    id: 'credit-notes',
    title: 'Credit notes',
    routes: ['/credit-notes'],
    modules: ['credit-notes'],
    body: [
      {
        kind: 'paragraph',
        text: 'A credit note is raised from an invoice that is awaiting payment or paid. You pick the invoice’s lines to credit, and the credit note starts as a Draft. Approving it is a separate step for Management and above.',
      },
      { kind: 'heading', text: 'Submitting and approving' },
      {
        kind: 'list',
        items: [
          'Accounts submits a Draft credit note for approval. It then waits as Awaiting approval, and can no longer be edited.',
          'Management approves it. Approving authorises the credit note in Xero and makes its value spendable against the company’s invoices.',
          'Neither submitting nor approving sends the credit note to the customer.',
        ],
      },
      { kind: 'heading', text: 'Crediting some of a line’s cities' },
      {
        kind: 'paragraph',
        text: 'A line with cities can credit just some of them. Cities, on the line, starts with all of the line’s cities, which credits the whole line. Pick fewer to credit only those.',
      },
      {
        kind: 'list',
        items: [
          'The amount is worked out pro-rata: a line with 6 cities crediting 2 of them credits 2 sixths of the line’s price.',
          'The amount can be edited, on the Draft credit note, like any other credit line.',
          'Only cities not already on another credit note for that invoice line can be picked. A voided credit note frees its cities again.',
        ],
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'Once the credit note is APPROVED, its cities are CANCELLED. They leave the Client Deliverables Tracker and Project Capacity. A line whose cities are all cancelled, or that was credited whole, no longer counts the company as sold on the project.',
      },
      {
        kind: 'list',
        items: [
          'In Xero, and so on the credit note’s PDF, the credit line lists the cancelled cities after its description.',
          'The invoice shows its cancelled cities on each line, and a line that is wholly cancelled shows Cancelled.',
          'A Draft, awaiting approval or voided credit note cancels nothing.',
        ],
      },
    ],
  },

  {
    id: 'projects',
    title: 'Projects and products',
    routes: ['/projects', '/products'],
    modules: ['projects', 'products'],
    body: [
      {
        kind: 'paragraph',
        text: 'A project is one sellable run: the products it sells, offered over a period, with planned capacity and targets, its regions of focus and the cities it runs in. Deals, quotes and invoices are raised against projects, line by line.',
      },
      {
        kind: 'paragraph',
        text: 'The period is a cycle, optionally narrowed to one intake within that cycle. Changing the cycle resets the intake, because an intake belongs to exactly one cycle.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Regions of focus and cities are two separate choices. The regions are never worked out from the cities — set both.',
      },
      { kind: 'heading', text: 'Capacity and targets' },
      {
        kind: 'paragraph',
        text: 'A project holds four numbers: Institution planned capacity, Institution target, Partner planned capacity and Partner target. Planned capacity is the places there really are; the target is the minimum aimed to sell. Any of them can be left blank, and blank means not set, not zero. The project’s capacity is the two planned capacities added together.',
      },
      {
        kind: 'paragraph',
        text: 'A company counts as a Partner when its company type is marked Partner in the Modules Catalog. Every other company, including one with no type, counts as an Institution.',
      },
      {
        kind: 'paragraph',
        text: 'Sold is the number of different companies with an accepted quote line or an invoice line that is not cancelled on the project. A company counts once however many lines or places it buys. Projects, the project page, Project Capacity and the Sales Tracker all count it this way, and read it as, for example, Institutions 12 of 30 · target 20 · Partners 3 of 5 · target 4.',
      },
      {
        kind: 'paragraph',
        text: 'Target met shows once sold reaches the target. Over capacity shows when more companies are sold than planned; selling past capacity is allowed.',
      },
      { kind: 'heading', text: 'Pricing' },
      {
        kind: 'paragraph',
        text: 'Pricing, on the project page, holds the project’s prices for each product it sells, in each currency — USD, GBP and EUR — and each price tier: Standard, Exclusive and Early bird. A product sold by city takes a per-city rate; any other product takes a flat price.',
      },
      {
        kind: 'list',
        items: [
          'A per-city rate can carry fixed prices for an exact number of cities, which replace the rate times the cities for that count.',
          'A flat price can carry fixed prices for an exact quantity. A fixed price is the whole line’s total, not the price of one.',
          'The document’s currency decides which currency’s prices a line uses.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Pricing is edited in place, in the grid, by the access levels that may edit projects, University Partnerships and above. Changes are recorded in the audit log. They apply to lines added or repriced afterwards; saved lines keep their prices.',
      },
      { kind: 'heading', text: 'The order is yours' },
      {
        kind: 'paragraph',
        text: 'Projects are listed in a manual order that you arrange, and that order is what the page opens on. Drag a row on a desktop; on a touch screen use the Move buttons, which do the same thing — a finger cannot drag a row at all.',
      },
      {
        kind: 'paragraph',
        text: 'Picking a column to sort by moves you off the manual order onto that column. Choose Manual order again to go back.',
      },
      {
        kind: 'paragraph',
        text: 'Arriving at Projects with nothing in the address bar opens on the cycle running today, and writes that cycle into the address — so it stays a normal filter you can clear or share.',
      },
      { kind: 'heading', text: 'Products' },
      {
        kind: 'paragraph',
        text: 'A product is the thing being sold, and its catalogue price is what a new line item starts from. Everybody can read the product list; changing it is Management and above, which is why the controls there are disabled for other levels with the reason beside them.',
      },
      { kind: 'heading', text: 'Campaign inclusion presets' },
      {
        kind: 'paragraph',
        text: 'A product can preset which campaign inclusions a new line starts with, and how many of each, by quantity band. A project can hold its own bands for any product it sells, under Campaign inclusion presets on the project page. When it does, lines under that project use only the project’s bands, and a quantity none of them covers presets nothing. A product with no project bands shows Uses product defaults, and its lines follow the product’s bands. Presets fill new lines and quantity changes only; saved lines are never changed.',
      },
    ],
  },

  /*
   * ⚠️ **`/operations` IS CLAIMED HERE AND BY NOTHING ELSE, DELIBERATELY.** The
   * Operations Dashboard puts three existing screens behind pills on one page;
   * this section covers the one that page opens on.
   *
   * ⛔ **Do not add `/operations` to the other two sections as well.**
   * `guideSectionIdForPath` matches the PATHNAME only — it never sees which pill
   * the query string names — so three sections claiming one path is a tie broken
   * by declaration order, and the drawer would open on the same section whatever
   * is on screen. That is a wrong answer that renders perfectly: no error, no
   * empty state, just guidance about a screen the reader is not looking at.
   */
  {
    id: 'client-deliverables',
    title: 'The Client Deliverables Tracker',
    routes: ['/client-deliverables', '/operations'],
    modules: [NAV_SURFACE_MODULE.trackerClientDeliverables],
    body: [
      {
        kind: 'note',
        tone: 'warning',
        text: 'Rows here are DERIVED from deal, quote and invoice line items. A row is a line item, so rows arrive by somebody adding a line to a deal, a quote or an invoice — the tracker reads the documents rather than holding its own list.',
      },
      {
        kind: 'paragraph',
        text: 'Every deal line shows on its own as a yellow Deal row until it is quoted or invoiced, when the quote or invoice row takes its place. Lines on lost deals are hidden. Progress typed on a Deal row carries forward onto the row that replaces it.',
      },
      {
        kind: 'paragraph',
        text: 'Rows are banded by project. Inside each band, Institutions list first and then Partners, by the company’s type, and each group reads alphabetically by company. A company with no type lists with the first type. Company, product, intake, regions, how many were promised, the document reference and the billing status are all read through the line, which is why they always agree with the document the customer was sent.',
      },
      {
        kind: 'paragraph',
        text: 'The columns you can type into — progress against what was promised, the dates, who is leading delivery, and the notes — are edited in place, in the row, and saved as you go.',
      },
      {
        kind: 'paragraph',
        text: 'Notes, beside Billing note, is the internal note on the line the row comes from. Editing it here changes the note on that quote, invoice or deal line, so both always read the same; it can be edited in the Google Sheet copy of the tracker too.',
      },
      {
        kind: 'paragraph',
        text: 'Inclusions lists the campaign inclusions on the row’s line, with how many of each, for example Email Campaign 20,000 candidates · Social Media Campaign 3 posts. It is read from the line and cannot be edited here or in the Google Sheet.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Delivering more than was promised is a real outcome, so the outstanding figure can legitimately be negative. It is not an error.',
      },
      {
        kind: 'paragraph',
        text: 'Cities cancelled by an approved credit note leave their invoice row. A row whose line is wholly cancelled leaves the tracker and its Google Sheet.',
      },
      {
        kind: 'paragraph',
        text: 'Add deliverable with no document adds a row for complimentary work, or work paid for out of another budget, that no quote or invoice covers. Work that has been sold reaches the tracker through its line item, so reach for this only when there really is no document behind it.',
      },
      { kind: 'heading', text: 'Project Capacity' },
      {
        kind: 'paragraph',
        text: 'Project Capacity shows each project’s companies against its cities. Inside each project, Institutions list first and then Partners, by the company’s type, alphabetically within each. A company with no type lists with the first type. The Excel export follows the same order and adds a Type column.',
      },
      {
        kind: 'paragraph',
        text: 'Each project’s heading shows Institutions and Partners sold against their planned capacity and target, with Target met or Over capacity where they apply. The Excel export carries the same heading. The Operations Dashboard shows the same panel.',
      },
      {
        kind: 'paragraph',
        text: 'A project marked Virtual event shows as a band of its own with a single Virtual column in place of cities.',
      },
      {
        kind: 'paragraph',
        text: 'A company that has the project only on a deal shows with yellow pending ticks. It counts as sold once it has an accepted quote line or an invoice line on the project.',
      },
      {
        kind: 'paragraph',
        text: 'Cities cancelled by an approved credit note are no longer ticked for that company. A company whose line is wholly cancelled no longer counts as sold.',
      },
      { kind: 'heading', text: 'The Google Sheets mirror' },
      {
        kind: 'paragraph',
        text: 'The tracker also appears as a Google Sheet, embedded in the page and openable in Google Sheets itself. Full screen gives the sheet the whole window, and Exit full screen brings the page back. SEED EMERGE refreshes the sheet every ten minutes.',
      },
      {
        kind: 'paragraph',
        text: 'These columns can be edited in the sheet and are copied back into SEED EMERGE on the next pass: POC, Specialist, Booking specifications, Completed, Start date, End date, Billing note, Notes and Ads plan. An edit to Notes changes the line item’s note, and nothing else on the quote or invoice.',
      },
      {
        kind: 'paragraph',
        text: 'POC, Specialist, Ads plan, Project and Region of Focus are dropdowns; Region of Focus takes more than one region. Start date and End date open a date picker. Completed and Promised take whole numbers, and Completed cannot be more than Promised. Clearing POC shows the client success manager in its place.',
      },
      {
        kind: 'paragraph',
        text: 'Project, Region of Focus and Promised can be changed in the sheet only on rows added in SEED EMERGE without a quote or invoice. On every other row they come from the line item, so change them there.',
      },
      {
        kind: 'paragraph',
        text: 'An edit SEED EMERGE cannot accept is refused, and the Sync notes column on that row says why. Rows typed into the sheet are not added to SEED EMERGE; they stay at the bottom of the sheet with a Sync note until they are cleared.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'If the same cell was also changed in SEED EMERGE since the last pass, SEED EMERGE wins and the sheet edit is lost. Every other column is rewritten from SEED EMERGE on each pass, so anything typed there is overwritten without warning. Comments you leave on the sheet survive.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'When too many rows change at once, the whole tab is held: nothing on it is saved, the Sync notes column marks the changed rows, and super admins are told. Fix or undo the change in the sheet and the next pass picks it up.',
      },
      {
        kind: 'paragraph',
        text: 'Sort by, above the sheet, changes the order of the sheet itself for everybody who opens it, and is set by Management and above.',
      },
    ],
  },

  /*
   * Written to the owner's decisions of 2026-10-12 (the tracker became a view of
   * deals), while that rebuild was in flight in another session. Re-read the
   * page against these sentences once it lands.
   */
  {
    id: 'sales-tracker',
    title: 'The Sales Tracker',
    routes: ['/sales-tracker'],
    modules: ['sales-tracker'],
    body: [
      {
        kind: 'paragraph',
        text: 'The Sales Tracker is a view of deals. Each deal appears once for every project its line items name, banded by project, so a deal selling two projects sits in both bands. A deal whose lines name no project sits in the No project band.',
      },
      {
        kind: 'paragraph',
        text: 'Inside each band, Institutions list first and then Partners, by the company’s type, alphabetically by company within each. A company with no type lists with the first type.',
      },
      {
        kind: 'paragraph',
        text: 'Each band heading shows Institutions and Partners sold against the project’s planned capacity and target, counted the same way as on Projects, and Quoted: the companies with a sent quote line on the project that are not yet sold. The Google Sheet copy carries the same band headings.',
      },
      {
        kind: 'paragraph',
        text: 'For 2026 - 27, companies that had a quote or invoice on a project but no deal for it were given one deal each, named Company name - Booked - 2026 - 27, with a line for each such project. Its stage follows the strongest document behind it: won for an invoice, then quote signed, then quote sent. A system note on the deal names those documents.',
      },
      {
        kind: 'paragraph',
        text: 'A deal’s stage is edited straight on the row. New deal, beside Columns, opens the deal form, and a deal you create there appears on the tracker in the band of each project its lines name.',
      },
      {
        kind: 'paragraph',
        text: 'A company’s projects of interest are a list on the company; the tracker reads its rows from deals.',
      },
    ],
  },

  /*
   * Prospect sub-stages, follow-up windows and resurfacing — owner's decisions
   * of 2026-10-23. The sub-stages are rows in the stage-status master with
   * behaviour flags, so an admin may rename them: the list below names the
   * ones SEED EMERGE starts with, and says so, rather than promising them.
   */
  {
    id: 'prospect-follow-ups',
    title: 'Prospect sub-stages and follow-ups',
    routes: ['/prospect-follow-ups'],
    modules: ['trackers-prospect-follow-ups', 'companies'],
    body: [
      {
        kind: 'paragraph',
        text: 'A Prospect company carries a sub-stage — its stage status — saying how warm it is. Each sub-stage decides what happens next. SEED EMERGE starts with these:',
      },
      {
        kind: 'list',
        items: [
          'Strongly Interested — the company must be contacted within 7 days.',
          'Interested — within 10 days.',
          'Interested Undecided — within 14 days.',
          'LOB (Lack of Budget) — the company comes back on 1 March, just before the next cycle starts on 1 April. A follow-up task for the account lead is created, due that day. The date can be changed when the sub-stage is picked.',
          'Interested In Near Future — asks for a follow-up date, with quick picks of 3, 6, 9 or 12 months or a date of your choice. A follow-up task for the account lead is created for that date, and stays out of the Tasks list and My day until the day it is due.',
          'Not Interested at All — asks for a reason, which is saved as a system note on the company. The company is left out of follow-ups, and its contacts stop receiving marketing emails.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'The window is counted from the later of the last contact and the moment the sub-stage was set, so picking a sub-stage again starts the clock afresh. Changing the follow-up date on the company moves its follow-up task to match.',
      },
      {
        kind: 'paragraph',
        text: 'Management and above set what each sub-stage does in Modules Catalog, under the Prospect company stage: the number of days allowed before contact, whether it asks for a follow-up date or brings the company back before the next cycle, whether it asks for a reason, whether it is left out of follow-ups, and whether it stops marketing emails.',
      },
      { kind: 'heading', text: 'What counts as contact' },
      {
        kind: 'list',
        items: [
          'An email logged against the company — see Logging emails.',
          'A note written by a person on the company or one of its deals. Notes SEED EMERGE writes itself do not count.',
          'A task on the company or one of its deals marked done.',
          'A quote for the company first sent to the client.',
          'A quote of the company sent for signature.',
        ],
      },
      { kind: 'heading', text: 'The Prospect Follow-ups Tracker' },
      {
        kind: 'paragraph',
        text: 'Prospect Follow-ups, under Trackers, lists the Prospect companies with their sub-stage, account lead, last contact and when the next contact is due, sorted with the most overdue first. Each company sits in one bucket:',
      },
      {
        kind: 'list',
        items: [
          'Overdue — the contact window has passed.',
          'Due soon — the window closes within 3 days.',
          'On track — the window closes later than that.',
          'Resurfacing — a company waiting for its follow-up date, from 30 days before that date.',
          'Waiting — a company whose follow-up date is more than 30 days away.',
          'No sub-stage — a Prospect without a sub-stage yet.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Opening the tracker shows the companies that need attention: Overdue, Due soon, On track and Resurfacing. Pick Waiting or No sub-stage to see those. Mine narrows the list to the companies you are account lead for, and the other filters narrow by account lead, sub-stage and name.',
      },
      { kind: 'heading', text: 'Notifications' },
      {
        kind: 'list',
        items: [
          'When a contact window is missed, the account lead is told straight away, once. Each day after that while the company is still overdue, it appears in their daily summary.',
          'When a waiting company starts resurfacing, 30 days before its follow-up date, the account lead is told.',
          'On the follow-up date itself, the account lead is told again.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Managers who want these too can be added in Settings → Notifications.',
      },
    ],
  },

  /*
   * The BCC email log — owner's decisions of 2026-10-23.
   */
  {
    id: 'email-log',
    title: 'Logging emails',
    routes: ['/email-log'],
    modules: ['email-log', 'companies'],
    body: [
      {
        kind: 'paragraph',
        text: 'To log an email against a school, add emerge@seedglobaleducation.com in BCC when you write to it, and again on each reply you send in the conversation. SEED EMERGE reads that mailbox every few minutes.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Only email SENT by SEED EMERGE staff, and confirmed by Gmail as genuinely coming from seedglobaleducation.com, is logged against a company automatically. Other email involving staff, such as a reply a school sends you, goes to the Email Log as unmatched, for a person to assign to a company or ignore. Automatic messages, mailing lists and email between colleagues only are left alone.',
      },
      { kind: 'heading', text: 'How an email finds its company' },
      {
        kind: 'paragraph',
        text: 'For an email a member of staff sent, SEED EMERGE looks at the people outside SEED on it. It matches their addresses against the company’s contacts first, and then against the company’s web address and email domain. Once a conversation has a company, later messages in it go to the same company.',
      },
      {
        kind: 'paragraph',
        text: 'Each conversation is kept as one email trail on the company’s Attachments, updated with every new message. Newest message first, each with its date, time in UTC, sender, recipients and the files it carried. Files sent with an email are saved as attachments of their own, titled with the date, time, sender and file name.',
      },
      { kind: 'heading', text: 'Logging an older conversation' },
      {
        kind: 'paragraph',
        text: 'To log a conversation that was not copied to SEED EMERGE at the time, forward the email to emerge@seedglobaleducation.com from your own address, with no school address on the forward. SEED EMERGE files the original emails, with their own dates and senders, on the school they match, or in the Email Log if they match none or more than one. Each one is marked Forwarded, with who forwarded it and when.',
      },
      {
        kind: 'paragraph',
        text: 'To choose the school yourself, add [#<company id>] to the subject of the forward, for example “Fwd: Campus visit [#782]”. The company id is the number at the end of the company page’s address. Forwarding the same email twice does not log it twice.',
      },
      { kind: 'heading', text: 'The Email Log' },
      {
        kind: 'paragraph',
        text: 'An email that is not logged automatically, or that matches no company or more than one, waits in Email Log, under Trackers, and its sender is told. Open it, pick the company it belongs to, and the trail and its files are saved to that company. Ignore takes it off the list instead.',
      },
      {
        kind: 'paragraph',
        text: 'Management and University Partnerships can assign and ignore emails in the Email Log. Anybody who can read a company can read its emails and email trails.',
      },
    ],
  },

  {
    id: 'future-billables',
    title: 'The Future Billables Tracker',
    routes: ['/future-billables-tracker'],
    modules: [NAV_SURFACE_MODULE.trackerInvoice],
    body: [
      {
        kind: 'paragraph',
        text: 'Future Billables is work that has been agreed and not yet billed: a row for every quote or invoice somebody has planned, before any document exists for it.',
      },
      {
        kind: 'paragraph',
        text: 'Plan a document adds a row. A row records the company, the services, the currency, the amount, the dates the work is planned for, and a comment. The dates and the comment are edited in the row; the rest is edited in the dialog.',
      },
      {
        kind: 'paragraph',
        text: 'The tracker has two tabs, Pending and Completed, each with its count. Pending holds the rows still to be raised. Completed holds the rows that are done.',
      },
      {
        kind: 'paragraph',
        text: 'The tick marks a planned row Completed and stops its reminder emails. Nothing is raised and nothing is deleted. Reopen, in the Completed tab, moves a ticked row back to Pending.',
      },
      {
        kind: 'paragraph',
        text: 'Raising the document from the Accounts Dashboard also completes the row. A raised row links to its document and cannot be reopened.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'A PLANNED ROW IS THE ONLY COPY OF WHAT IT RECORDS. Deleting one takes its services, currency, amount and dates with it, and there is no undo. No quote or invoice is affected, even when the row was raised.',
      },
      {
        kind: 'paragraph',
        text: 'Export these rows sends exactly what your filters describe in the tab you are on, whichever page you happen to be looking at.',
      },
      {
        kind: 'paragraph',
        text: 'The Future Billables Google Sheet keeps every planned row, with a Status column that reads Pending or Completed.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Two layers of filtering, and they do different things. The bank of filters above the table narrows the whole tracker. The small funnels on a column heading narrow only the page in front of you, and the line beside them says how many rows they are hiding.',
      },
      {
        kind: 'paragraph',
        text: 'Every amount is shown with its own currency and is never converted, so figures here are read row by row.',
      },
      {
        kind: 'paragraph',
        text: 'The Pending rows also appear as the Future Billables section of the Accounts Dashboard, which is where a planned row becomes a real quote or invoice.',
      },
    ],
  },

  {
    id: 'revenue-tracker',
    title: 'The Revenue Tracker',
    routes: ['/revenue-tracker'],
    modules: [NAV_SURFACE_MODULE.trackerRevenue],
    body: [
      {
        kind: 'paragraph',
        text: 'The Revenue Tracker is a cross-tab rather than a list: a block of rows per company, gathered into bands by billing region, with a group of four columns for every cycle — Service Name, Amount, Total and Notes.',
      },
      {
        kind: 'paragraph',
        text: 'The company name stays put while you scroll sideways. Notes is the column you can type into; everything else is read from the quotes and invoices behind it, which is why they always agree with the documents.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'THE COLUMNS DO NOT ADD UP TO TOTAL REVENUE. Paid lines whose dates fall inside no cycle window appear in no column at all, and the banner above the table says how many there are.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Where a cycle or a band holds more than one currency the total reads Mixed currencies rather than a number, and the amounts beside each service stay exact. A figure is never made up by adding currencies together.',
      },
      {
        kind: 'paragraph',
        text: 'Search companies, Billing region and Cycle narrow what is on screen — a cycle filter hides columns rather than rows. While a company search is running the closing totals are hidden, because those are the figures for every company in a band and would not match the rows in front of you.',
      },
      {
        kind: 'paragraph',
        text: 'The tracker also appears as a Google Sheet, and Open in Google Sheets opens it. The sheet is rewritten from SEED EMERGE, so make the change on the record.',
      },
    ],
  },

  /* Product Insights → the layout every product page shares — overhaul 2026-10-19. */
  {
    id: 'product-insights-layout',
    title: 'Product Insights: finding your way around',
    routes: ['/product-insights'],
    modules: ['product-insights'],
    body: [
      {
        kind: 'paragraph',
        text: 'Every Product Insights page is laid out the same way, so once you know one you know them all.',
      },
      {
        kind: 'list',
        items: [
          'The pages are grouped by SEED’s three stages, the same as on universities.seedglobaleducation.com: Attract (Events, School Profiles, Challenge Hub), Nurture (In-Country Representation, Lead & Application Engagement) and Convert (Funnel Analytics & BI, Scholarships, AI Visa Tutor). The sidebar and the row of buttons under each page title follow these groups. Digital Advertising and Edumate are marked Soon: they have no page yet.',
          'One toolbar holds the cycle, the filters and the search. Each active filter shows as a chip you can remove, and Reset clears them all.',
          'The headline tiles show the main figures. A tile that highlights when pressed is a filter: press it again to clear it.',
          'Tabs split the page, with a count beside each one.',
          'Tables sort when you press a column heading. A row with an arrow opens to show its breakdown, and Show more loads further rows.',
          'A school linked to a company shows the company’s name as a link to its page in SEED EMERGE.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'The filters, the tab and the search are kept in the page address, so a copied link opens the same view.',
      },
    ],
  },

  /* Product Insights → SEED FAB — owner's rulings 2026-10-18. */
  {
    id: 'product-insights-fab',
    title: 'Product Insights: Funnel Analytics & BI',
    routes: ['/product-insights/fab', '/product-insights/scholarships'],
    modules: ['product-insights'],
    body: [
      {
        kind: 'paragraph',
        text: 'Funnel Analytics & BI shows the student funnel for the client schools on Funnel Analytics & BI, one cycle at a time, picked in the toolbar. Funnel Analytics & BI cycles run 1 June to 31 May and are not SEED EMERGE cycles.',
      },
      {
        kind: 'list',
        items: [
          'Overview: the funnel from scholarship applications to school applications, admits and enrolled, with the conversion between stages, and breakdowns by region, source, first product, top schools and top scholarships.',
          'Schools: applications, admits and enrolled per school, with admit and enrol rates, a breakdown by region, source or first product, and a search by name or website.',
          'School links: which Funnel Analytics & BI schools are linked to a company in SEED EMERGE.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'The school stages are cumulative: a student who enrolled is also counted under Admits and School applications. Regions and countries are the applicant’s country of residence.',
      },
      {
        kind: 'paragraph',
        text: 'Region, Source and First product sit in the toolbar and apply to every tab. More filters holds countries, dates, intakes, scholarship round, evaluation and submission.',
      },
      {
        kind: 'paragraph',
        text: 'Scholarships is its own Product Insights page over the same Funnel Analytics & BI data and filters: applications per scholarship and its share of the total, optionally split by region. Top scholarships on the Overview opens it.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'The numbers are read live from Funnel Analytics & BI and refresh every 15 minutes. SEED EMERGE keeps no history of them, so an earlier figure cannot be looked up later.',
      },
      {
        kind: 'paragraph',
        text: 'School links lists the unlinked schools first. Each unlinked school offers a suggested company, matched on website first and then on name, with a matching country ranked higher. Nothing is linked until somebody presses Link, one school at a time or all website matches at once; Unlink removes a link. Only Management can link or unlink a school.',
      },
      {
        kind: 'paragraph',
        text: 'A linked school’s applications, admits and enrolled also appear in the Product Insights section of the company’s page, alongside the company’s figures from the other Product Insights pages.',
      },
      {
        kind: 'paragraph',
        text: 'Management and University Partnerships can see Funnel Analytics & BI.',
      },
    ],
  },

  /* Product Insights → Events, LAE and ICR — owner's rulings 2026-10-18. */
  {
    id: 'product-insights-events',
    title: 'Product Insights: Events',
    routes: ['/product-insights/events'],
    modules: ['product-insights'],
    body: [
      {
        kind: 'paragraph',
        text: 'Events shows SEED’s student events for a cycle, virtual and in person, unpublished events included: registrations, attendees, the attendance rate, and the schools that took part, split into Institutions and Partners.',
      },
      {
        kind: 'paragraph',
        text: 'For an in-person event, a school’s reach is the registrants scanned at its booth or session. A virtual event shares one audience, so every school on it shows the event’s registrations as shared reach.',
      },
      {
        kind: 'paragraph',
        text: 'The By school view adds each school’s events, booth connections, presentation attendees and reach across the cycle.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'The numbers are read live from SEED’s student platform and refresh every 15 minutes.',
      },
    ],
  },
  {
    id: 'product-insights-lae',
    title: 'Product Insights: Lead & Application Engagement',
    routes: ['/product-insights/lae'],
    modules: ['product-insights'],
    body: [
      {
        kind: 'paragraph',
        text: 'In Lead & Application Engagement, SEED counsellors work each client school’s lead lists by call, WhatsApp and email and push students to apply. This page shows that work school by school, the same way the LAE app’s Productivity dashboard does for schools.',
      },
      { kind: 'heading', text: 'The controls' },
      {
        kind: 'list',
        items: [
          'Calls: All calls, System (dialled through the LAE dialler) or Manual (logged by hand by the counsellor).',
          'Period: Day, 7 days, 30 days, 12 weeks or Custom. Day adds arrows to step back and forward a day; Custom adds From and To dates. Up to 31 days is shown day by day, a longer period week by week.',
          'Cycle: an LAE assignment cycle, such as 2026-27, or All cycles. The page opens on the current cycle, the one running 1 April to 31 March that contains today.',
          'Funnel: All funnels, Prospect to application, or Admit to enrolment.',
          'A changed cycle, funnel or calls choice shows as a chip under the controls, and Reset puts all three back.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Every day and time on the page is India time. The filters live in the address bar, so a link you copy opens on the same view.',
      },
      { kind: 'heading', text: 'Key figures, Trend and Schools' },
      {
        kind: 'list',
        items: [
          'Key figures: the totals for the period across every school, each with its change against the previous period of the same length, and a small chart of its daily or weekly run where one applies.',
          'Trend: one metric as a bar chart, day by day or week by week. Pick Total Calls Dialled, Positive Status Changes, Negative Status Changes, Applications, Leads Worked On or Messages. Hover a bar to read its figures; click a bar to narrow the whole page to that day or week.',
          'Schools: one row per school and funnel, with its assignments, specialists, calls, talk time, interested leads, positive and negative changes and applications. The Total row at the foot is the whole table, with each student counted once.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Split by assignment shows each school’s assignments as lines inside its row, with the school’s subtotal under them in bold. Untick it for one line per school.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'With Split by assignment on, the assignment lines can add up to more than the school total. Each person is counted once per school, but the same student can sit in several assignments and is counted on each of them.',
      },
      {
        kind: 'paragraph',
        text: 'Export saves the table as it stands — your filters and sort included — to an Excel file. Call times in the file are in seconds and rates are percentages. The grand total is included only when no filter has dropped a school.',
      },
      { kind: 'heading', text: 'Filtering and arranging' },
      {
        kind: 'list',
        items: [
          'Every column header has a filter. A number column also takes a condition: greater than, at least, less than, at most, equal to, or between two values. Time columns are filtered in minutes.',
          'Columns shows, hides and freezes columns. School and Assignment always stay on the left.',
          'Edit layout in the page header moves, resizes or hides Key figures, Trend and Schools, the same as on any other arranged page.',
        ],
      },
      { kind: 'heading', text: 'A school’s own view' },
      {
        kind: 'paragraph',
        text: 'Click a school’s row to open it. The window shows that school and funnel for the same period: its key figures, outcomes, activity, the best hours for positive outcomes, the hours its leads changed status, its counsellors, its assignments and its status moves. Changing the period or the calls choice there changes the page behind it too. The window has its own Edit layout.',
      },
      {
        kind: 'paragraph',
        text: 'When a school is mapped to a company in SEED EMERGE, its name is a link to that company’s page.',
      },
      { kind: 'heading', text: 'What the figures mean' },
      {
        kind: 'paragraph',
        text: 'Unless a definition says otherwise, a count of students counts unique people: a student who appears in several assignments of the same school, cycle and funnel is counted once, and a status change synced to the same student in other assignments is not counted again. Positive and negative changes are decided by the Positive and Negative tags on statuses in the LAE Outcome library.',
      },
      {
        kind: 'list',
        items: [
          'Total Leads (Leads Assigned): students currently assigned. This is the present total, not limited to the period.',
          'Active Schools: schools with at least one call or worked lead in the period, out of all the schools listed.',
          'Avg Leads per School: Total Leads divided by the number of schools.',
          'Leads Contacted: assigned students who were called or contacted at least once in the period.',
          '% Leads Contacted: Leads Contacted as a share of the students assigned.',
          'Leads Worked On: every student called or contacted in the period, whether or not they are assigned.',
          'Total Calls Dialled: every call dialled in the period, whether or not the student picked up.',
          'Calls Answered: calls the student picked up. Answer Rate is the share of calls dialled that were answered.',
          'Total Dial Time: from pressing dial to hanging up, summed over every call, ringing included.',
          'Total Call Time: the length of every call dialled, answered or not, ringing not included.',
          'Talk Time: time spent talking on answered calls.',
          'Avg Call Time: talk time on answered calls divided by the number of answered calls. Only answered calls count, so it is not Total Call Time divided by Calls Answered.',
          'Marked as Interested in 2027 Intakes: leads whose status changed to Interested in 2027 Intakes in the period (Counselling Completed, its earlier name, also counts), credited to whoever made the change.',
          'Positive Status Changes: leads whose status changed to one tagged Positive, such as Interested in 2027 Intakes or Application Submitted.',
          'Negative Status Changes: leads whose status changed to one tagged Negative, such as Not Interested. Lower is better.',
          'Total Call Time and Avg Call Time with "Interested in 2027 Intakes", "Positive Changes" or "Negative Changes": the same measures, over answered calls with the leads the counsellor moved to that status in the period.',
          'Applications: students who started their first application in the period, each counted once per school across all its assignments.',
          'Reached Deposit / Visa: students who reached a late stage in the period — deposit paid, I-20 issued, visa or enrolment.',
          'Whatsapps Sent: WhatsApp messages sent from LAE, counted when the counsellor opens the message in WhatsApp; delivery is not confirmed.',
          'Emails Sent: emails sent to students from LAE in the period.',
          'Messages (Trend): Whatsapps Sent and Emails Sent together.',
          'Tasks Completed: follow-up tasks marked done in the period.',
          'Specialists: the counsellors on an assignment, shown as those who worked out of all of them. A dot marks a counsellor who worked in the period.',
          'Leads Called and Leads Spoken To (counsellors): students called at least once, and students who picked up at least one call.',
          'Manual Calls, Manual Connected and Manual Talk Time (counsellors): calls the counsellor logged by hand, those the student picked up, and their timed length. Manual Calls counts manual calls whatever the Calls choice.',
          'Student Sentiment, % Positive Calls, Hot Leads and Call Quality (counsellors): from calls reviewed automatically. Sentiment runs from −1 to +1; Call Quality is scored 0 to 100.',
          'Occupancy (counsellors): the share of available working time spent on calls and tasks. Available Hours are scheduled hours minus leave, holidays and non-calling time; Busy Hours are call time plus the time counted for messages and status changes.',
          'Days Active (counsellors): days with at least one call or activity, in India time.',
        ],
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'The numbers are read live from SEED’s student platform and are at most a minute old. Updated in the page header shows when they were fetched, and its button fetches them again.',
      },
    ],
  },
  {
    id: 'product-insights-icr',
    title: 'Product Insights: In-Country Representation',
    routes: ['/product-insights/icr'],
    modules: ['product-insights'],
    body: [
      {
        kind: 'paragraph',
        text: 'With In-Country Representation, a SEED representative in a country files a monthly report for each university they represent.',
      },
      {
        kind: 'paragraph',
        text: 'Per university and representative country it shows leads generated and engaged, interest in the next intake, applications, admits, offers accepted and enrolled. A row opens to the month-by-month figures.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'The figures are reported by the representatives themselves, and each report is cumulative.',
      },
    ],
  },

  /* Product Insights → AI Visa Tutor, Challenge Hub and School Profiles — owner's rulings 2026-10-18. */
  {
    id: 'product-insights-visa-tutor',
    title: 'Product Insights: AI Visa Tutor',
    routes: ['/product-insights/visa-tutor'],
    modules: ['product-insights'],
    body: [
      {
        kind: 'paragraph',
        text: 'AI Visa Tutor is SEED’s AI mock visa interview. A client school or university buys licences for its admitted students, who practise interviews and report their visa outcome.',
      },
      {
        kind: 'paragraph',
        text: 'Per client it shows licences bought, allocated and activated, students practising, sessions, scores, and visa approvals and rejections. Destination countries are shown separately.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Licence counts are the stock right now; activity and outcomes are counted for the selected cycle.',
      },
    ],
  },
  {
    id: 'product-insights-challenges',
    title: 'Product Insights: Challenge Hub',
    routes: ['/product-insights/challenges'],
    modules: ['product-insights'],
    body: [
      {
        kind: 'paragraph',
        text: 'Challenge Hub covers the SEED Hackathon and the SEED Business Challenge, run with partner schools. Each edition shows its participants, teams, finalists, winners and partner schools.',
      },
      {
        kind: 'paragraph',
        text: 'Later applied, admitted and enrolled count participants who went on to do so afterwards, at any school and at the partner schools. It is a correlation, not proof that the challenge caused it.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Challenge data is a one-off import; the date it was taken is shown on the page.',
      },
    ],
  },
  {
    id: 'product-insights-school-profiles',
    title: 'Product Insights: School Profiles',
    routes: ['/product-insights/school-profiles'],
    modules: ['product-insights'],
    body: [
      {
        kind: 'paragraph',
        text: 'School Profiles is a client school’s public profile page on SEED’s Course Finder, on a Basic or Premium subscription.',
      },
      {
        kind: 'paragraph',
        text: 'Per school it shows profile views, visitors, engaged students and their interactions: showing interest, scholarship and meet-the-directors requests, favourites, unlocks and apply clicks. A row opens to its top programmes and student countries.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Profile views are reliable from 9 September 2026 onwards.',
      },
    ],
  },

  /*
   * ⚠️ **NO GATE — the THIRD ungated section, and the one that makes the
   * never-empty invariant comfortable rather than tight.** Reading the
   * reference lists is open to every access level, and `/catalog` carries no
   * sidebar row of its own to hide, so there is nothing here to gate on.
   */
  {
    id: 'catalog',
    title: 'Modules Catalog and the reference lists',
    routes: ['/catalog', '/cycles', '/intakes'],
    body: [
      {
        kind: 'paragraph',
        text: 'Modules Catalog indexes the lists the rest of SEED EMERGE picks from, under Managed lists: regions of focus, cities, campaign inclusions, currencies and countries, the deal, company and quote stages, invoice statuses, deal types and lead sources, booking statuses, travel packages and ads plans. Cycles and intakes are there, and so are contact groups, billing region defaults, target periods and group targets.',
      },
      {
        kind: 'paragraph',
        text: 'Stage statuses are listed under the company stage they belong to. Each stage has its own list, which starts empty; add, rename, reorder or retire statuses there. A status cannot be moved to another stage: delete it and add it again under the other stage. A status that a company still holds can only be retired, not deleted. Every stage with statuses gets its own status column on the companies lists and in the Companies Google Sheet.',
      },
      {
        kind: 'paragraph',
        text: 'A stage status can also carry behaviour: the days allowed before the company must be contacted, a follow-up date or a return before the next cycle, a reason asked for when it is picked, leaving the company out of follow-ups, and stopping marketing emails to its contacts. The Prospect sub-stages use these — see Prospect sub-stages and follow-ups.',
      },
      {
        kind: 'paragraph',
        text: 'A company type can be marked Partner. Companies of a Partner type count as Partners against a project’s capacity and targets; every other company counts as an Institution.',
      },
      {
        kind: 'paragraph',
        text: 'Price tiers lists the tiers a line can be sold at — Standard, Exclusive and Early bird. Standard is the default a new line starts on, and the tier a project’s price falls back to.',
      },
      {
        kind: 'paragraph',
        text: 'Contacts sits below that, under Records without a sidebar row — it is a real page reached from here or from the company that holds the person.',
      },
      { kind: 'heading', text: 'Company mapper' },
      {
        kind: 'paragraph',
        text: 'Company mapper ties the schools, universities and partners on the SEED platform to SEED EMERGE companies, so their Product Insights figures land on the right company. It shows to whoever can read Product Insights, in three tabs — Needs mapping, Mapped and All — with a search.',
      },
      {
        kind: 'list',
        items: [
          'A platform record that needs mapping offers suggested companies, matched on website and then on name. Nothing is linked until somebody presses Link.',
          'Link ties the record to a company you pick. Several platform records can share one company.',
          'Create company opens the company form filled in from the platform record. The new company goes for approval like any other and is linked once it is saved.',
          'Unlink removes a link, after you confirm.',
          'When linking, Also set Company name to the platform’s name renames the company in SEED EMERGE as well. It is never ticked for you, it needs permission to edit companies, and it leaves the Billing name and Xero untouched.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Product Insights figures follow these links. Where a Funnel Analytics & BI school link and a platform link point to different companies, the school link wins. If some SEED platform data could not be read, the mapper lists what it did read and says which sources failed.',
      },
      {
        kind: 'paragraph',
        text: 'Every access level can read these lists. Changing one is Management and above, and the controls say so where they are not available to you.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'RETIRE A VALUE, DO NOT DELETE IT. Deactivating stops the value being offered on new records while every record already holding it keeps working and keeps reading correctly. Deleting removes it outright and is only safe for something nothing has ever used.',
      },
      {
        kind: 'paragraph',
        text: 'Active and retired values are both listed here, so a retired one stays visible to whoever looks after the list. Cycles and intakes are the exception: those are removed by deleting, and a cycle that still has intakes says so and points you at them.',
      },
    ],
  },

  {
    id: 'xero',
    title: 'Xero',
    routes: ['/integrations'],
    modules: ['integrations'],
    body: [
      {
        kind: 'paragraph',
        text: 'Xero holds the accounts. SEED EMERGE links its records to Xero contacts and documents so the two describe the same thing, and shows the state of that link on each record.',
      },
      {
        kind: 'note',
        tone: 'info',
        text: 'Your browser never talks to Xero directly. SEED EMERGE holds the connection on everybody’s behalf, so Xero works or fails for the whole office at once rather than person by person.',
      },
      {
        kind: 'paragraph',
        text: 'A quote or an invoice reaches Xero by being saved. A quote that has not reached Xero also offers Send to Xero and preview in its PDF preview window. Companies and contacts carry Create in Xero and Send to Xero on the record, for the cases where a link has to be made or remade by hand.',
      },
      {
        kind: 'paragraph',
        text: 'A company appears in Xero under its Billing name, and its quotes, invoices and credit notes name the customer by the Billing name too. Changing the Billing name updates Xero; changing the Company name does not.',
      },
      {
        kind: 'paragraph',
        text: 'Where a document cannot be sent, Xero says why and SEED EMERGE shows that reason as written, with a link to whichever record needs the fix — usually a tax setting on the company or a detail on one line.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'Where a record is already linked to Xero, do not unlink it to get an update across. Unlinking and sending again produces a SECOND document in the accounts, and the original is left behind.',
      },
      {
        kind: 'paragraph',
        text: 'Integrations holds three panels: Xero, What is linked — the running count of records tied to Xero — and DocuSign. Reading the page is open to every access level; Connect Xero and Disconnect are for the levels that may edit companies, which is University Partnerships and above.',
      },
    ],
  },

  {
    id: 'docusign',
    title: 'Sending a quote for signature',
    /*
     * Gated on `quotes` rather than on a tracker surface: every DocuSign
     * control this describes hangs off a quote — `Send for signature` on the
     * quote and on the quotes list — so a reader who is not offered quotes has
     * nowhere to apply any of it.
     */
    modules: ['quotes'],
    body: [
      {
        kind: 'paragraph',
        text: 'Send for signature puts a quote in front of a signer through DocuSign, from the quote itself, as a row action on the quotes list, or from a quote’s PDF preview. Once it is out you can watch its status, get a signing link, remind the signer, change who is signing, or void the envelope.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'A COMPLETED SIGNATURE MOVES THE QUOTE TO THE ACCEPTED STAGE, AND NOBODY IS ASKED TO CONFIRM. It raises no invoice — billing is still Convert to invoice or Bill line items, taken by a person afterwards.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'Read the badge on the send screen before you send. When the DocuSign connection is not a production one, SEED EMERGE says so and warns that the signature carries no legal force — and the stage move still happens regardless. A test signature looks exactly like a real one.',
      },
      {
        kind: 'paragraph',
        text: 'A signing link is meant for a signer who is with you: it is good once and expires within a few minutes, so generate it at the moment it is needed rather than emailing it on.',
      },
      {
        kind: 'paragraph',
        text: 'Returning from the signing screen is not proof of anything by itself. Check status is what asks DocuSign, and the status it reports is the answer.',
      },
    ],
  },

  {
    id: 'tasks-notes',
    title: 'Tasks, notes and reminders',
    routes: ['/tasks'],
    /*
     * ⚠️ **TWO areas, and they are genuinely different questions.** `tasks`
     * governs the records — the drawer on every company, deal, quote and
     * invoice — while the tracker surface governs the Tasks Tracker row and its
     * page. A reader offered either one has something in this section to use,
     * which is what the union is for.
     */
    modules: ['tasks', NAV_SURFACE_MODULE.trackerMyTasks],
    body: [
      {
        kind: 'paragraph',
        text: 'Companies, deals, quotes and invoices each carry tasks and notes, opened from the record in a slide-over. The button shows what is behind it, so the count tells you whether opening it is worth doing. Every access level can write a task or a note.',
      },
      {
        kind: 'paragraph',
        text: 'Tasks Tracker in the sidebar is your own work, in two views. My day is the brief for today and holds only what you owe — a task you set for a colleague is in All tasks, never here.',
      },
      {
        kind: 'paragraph',
        text: 'A task is due at a time worked out from the time zone recorded against the person it is assigned to, so a task set for somebody in another country falls due in theirs.',
      },
      {
        kind: 'paragraph',
        text: 'A follow-up task created by a Prospect sub-stage stays out of the Tasks list and My day until the day it is due. Tick Show upcoming on the Tasks list to see those waiting tasks as well.',
      },
      { kind: 'heading', text: 'Reminders' },
      {
        kind: 'note',
        tone: 'warning',
        text: 'Reminders come from the bell in the top bar and only run WHILE SEED EMERGE IS OPEN IN A TAB. Close the tab and nothing is watching. Turn them on, and press Send a test reminder to check they reach you, in the panel behind the bell.',
      },
    ],
  },

  /*
   * Ungated on purpose: every access level has a Notifications page of its own.
   * 2026-10-22 (owner): notifications follow the permission set.
   */
  {
    id: 'notifications',
    title: 'Notifications',
    routes: ['/notifications'],
    body: [
      {
        kind: 'paragraph',
        text: 'Notifications follow your permission set. Anything in an area your permission set does not include is off and cannot be switched on, and it would not be sent to you anyway. If your access changes, the choices you made earlier apply again.',
      },
      {
        kind: 'paragraph',
        text: 'Security notifications are always on. Team defaults for each access level follow the same rule.',
      },
    ],
  },

  /*
   * ⚠️ **`adminOnly`, and the ACCESS-LEVEL half of this section was moved OUT
   * of it into `access-level` above.** What a level means is the first thing a
   * reader on Operations wants and the last thing they would find behind an
   * admin gate; what is left here is the managing, which is Management and
   * above. The staff list itself is readable by everyone, and `access-level`
   * says so.
   */
  {
    id: 'managing-people',
    title: 'Managing people and access levels',
    routes: ['/users', '/permission-sets'],
    adminOnly: true,
    body: [
      {
        kind: 'paragraph',
        text: 'Users lists everybody with their access level and the groups they belong to. Add somebody there, set their level, and send them an onboarding email. Targets, on a row, is what that person is measured against on the Management Dashboard. Reporting currency, on a row, is the currency Targets & performance and Financial Analytics open in for that person.',
      },
      {
        kind: 'paragraph',
        text: 'An access level IS a permission set: each level points at the set of the same name, so changing somebody’s level on Users is what moves them onto a different set, and their access follows from that one change.',
      },
      { kind: 'heading', text: 'Editing the levels themselves' },
      {
        kind: 'paragraph',
        text: 'Permission Sets is where the levels are edited, and it is for Super Admin. Each set is a grid of areas down the side and actions across the top, and a set also names the page its holders land on after signing in.',
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'UNTICKING A BOX WRITES DENIED FOR EVERYBODY AT THAT LEVEL, and the change reaches them the next time they sign in — not while they are working. It changes what SEED EMERGE shows them, which is a different thing from protecting the records behind it.',
      },
      {
        kind: 'paragraph',
        text: 'Own only, offered beside All records as how wide a grant is, behaves the same as All records today, and the option says so where it is chosen.',
      },
      {
        kind: 'paragraph',
        text: 'Groups are a separate thing from an access level. They decide who may be named as an account lead, a client success manager or a delivery lead on a record, and who receives a company approval email. Membership is edited on the Users row.',
      },
    ],
  },

  {
    id: 'admin',
    title: 'Settings and the audit log',
    routes: ['/settings', '/audit-log'],
    // 2026-10-01: the two screens are gated by `settings` and `api-logs` now.
    // *SUPERSEDED: `adminOnly: true`.*
    modules: ['settings', 'api-logs'],
    body: [
      {
        kind: 'paragraph',
        text: 'Both are Management and above, on reading as well as on changing, which is why the rows are absent from the sidebar below that level.',
      },
      { kind: 'heading', text: 'Settings' },
      {
        kind: 'paragraph',
        text: 'Settings holds Default quote terms — the wording that goes onto a new quote — the state of the mail server with a test send, and support sessions.',
      },
      { kind: 'heading', text: 'Exchange rates' },
      {
        kind: 'paragraph',
        text: 'Settings → Advanced → Exchange rates chooses where the rates used by the reports come from: the daily source, a fallback for days the daily source has no rate, and the source used to convert at the end of a period. The sources are the SEED currency service, XE and Xero.',
      },
      {
        kind: 'list',
        items: [
          'Xero has no rate service of its own. Choosing Xero reads the rate Xero stamped on each of its documents in another currency, and keeps one rate per currency per day.',
          'A day with no Xero document in that currency has no Xero rate, so Xero needs a fallback source: the fallback fills those days.',
          'Refresh fetches the latest rates now, rather than waiting for the next scheduled pass.',
        ],
      },
      {
        kind: 'note',
        tone: 'warning',
        text: 'Email failures are silent everywhere else in SEED EMERGE by design — a forgotten-password request and a company approval round both look the same whether or not the message went out. This page is where a dead mail server shows, so a test message that arrives in the inbox is worth more than one that merely reports success on screen.',
      },
      { kind: 'heading', text: 'The audit log' },
      {
        kind: 'paragraph',
        text: 'Every request SEED EMERGE makes is recorded and readable here in plain English — who did what, to which record, and whether it worked. An attempt that was refused reads as an attempt, so a deletion that never happened is never described as one.',
      },
      {
        kind: 'paragraph',
        text: 'Routine noise — health checks, sign-in renewals — is folded away by default, and the page says how many rows it has folded rather than filtering itself quietly.',
      },
    ],
  },
];

/* --- who is offered which section ---------------------------------------- */

/**
 * May this reader be offered this section?
 *
 * ── Why the module test is ANY and not ALL ────────────────────────────────
 *
 * A section can be about several areas at once — *Deals, quotes and invoices*
 * is three — and a permission set may grant one of them and deny another. The
 * two honest designs were a gate on the SECTION and a gate on every BLOCK, and
 * the section wins on three counts:
 *
 *  - **The prose does not decompose.** Those three are one story: a quote is
 *    billed into an invoice. Gating block by block does not give a reader less
 *    to read, it gives them a section that stops in the middle of the sentence
 *    they came for.
 *  - **A per-block gate is a second permission map**, maintained by hand, in
 *    the file whose own header says nothing here typechecks. Two maps of who
 *    may see what agree the day they are written and drift afterwards.
 *  - **The costs are not symmetric.** Being told about a page you cannot open,
 *    inside a document you opened on purpose, costs a paragraph. Being shown a
 *    half-section costs the reader the answer.
 *
 * ⚠️ **What that buys is bounded, and saying so is the point**: these gates
 * hide a section an administrator has already hidden the ROW for. They are a
 * view decision, exactly as `isNavItemOffered` is — the records behind every
 * one of these modules answer whoever asks for them, so nothing built on this
 * may be worded as though the data were protected.
 *
 * ── Why this is not a second copy of `isNavItemOffered` ───────────────────
 *
 * That predicate takes a `NavItem`, which requires a `label` and an `icon`;
 * calling it from here would mean fabricating both, which is data that lies
 * about what it is. And its `module` is a single slug, so it could not answer
 * the union above in any case. **The rule itself is not duplicated**: the three
 * questions below — `isAdmin`, `isSuperAdmin`, `canRead` — are the same three
 * that predicate asks, and every one of them is answered by `can()` in
 * `@/lib/permissions`, which is where the rule lives for both callers.
 *
 * ⛔ **It DEGRADES OPEN and must keep doing so.** `ability.canRead` denies only
 * on an explicit `Denied`; an older API, an unseeded set, or a permission fetch
 * that has not landed leaves everything visible. A guide that emptied itself
 * while permissions loaded would read as an app that is broken.
 */
export function isGuideSectionOffered(section: GuideSection, ability: Ability): boolean {
  if (section.adminOnly && !ability.isAdmin) return false;
  if (section.superAdminOnly && !ability.isSuperAdmin) return false;
  const { modules } = section;
  if (modules === undefined || modules.length === 0) return true;
  return modules.some((moduleSlug) => ability.canRead(moduleSlug));
}

/**
 * The sections this reader is offered, in order.
 *
 * ⚠️ **THE GUIDE MUST NEVER COME BACK EMPTY.** A drawer with nothing in it, and
 * an untouched filter box above it, reads as an app that has broken rather than
 * as an access level that is narrow. Three sections carry no gate at all and
 * are what guarantee it — `getting-around`, `access-level` and `catalog` — each
 * of which says so at its own definition. **Before adding a gate to one of
 * those, check another section is still ungated.**
 *
 * There is deliberately no "if this came back empty, show everything" fallback:
 * that would turn a mistake into a silent one, and this invariant is meant to
 * be readable rather than papered over.
 */
export function guideSectionsFor(ability: Ability): readonly GuideSection[] {
  return GUIDE_SECTIONS.filter((section) => isGuideSectionOffered(section, ability));
}

/* --- lookups ------------------------------------------------------------- */

/*
 * Built once per section and cached, because the drawer runs it over every
 * section on every keystroke in the filter box.
 *
 * A `WeakMap` keyed by the section object rather than by `id`: the sections are
 * module constants, so the entries live as long as the module, and nothing has
 * to be invalidated if a section is ever swapped at runtime.
 */
const searchTextCache = new WeakMap<GuideSection, string>();

function blockText(block: GuideBlock): string {
  return block.kind === 'list' ? block.items.join(' ') : block.text;
}

/**
 * Everything in a section that a filter should match, case-folded.
 *
 * ⚠️ The TITLE is included as well as the body. Filtering on the body alone
 * makes a section invisible to somebody who typed its own heading, which is the
 * first thing anybody types.
 */
export function guideSearchText(section: GuideSection): string {
  const cached = searchTextCache.get(section);
  if (cached !== undefined) return cached;

  const text = [section.title, ...section.body.map(blockText)].join(' ').toLowerCase();
  searchTextCache.set(section, text);
  return text;
}

/**
 * The section covering a path, or `null` when nothing claims it.
 *
 * ⚠️ **Longest match wins, and the prefix must end at a `/`.** A bare
 * `startsWith` would let `/invoices` claim `/invoice-tracker`, and a plain
 * equality test would fail on every detail page. Both failures are silent: the
 * drawer simply opens on the wrong section, which reads as a design choice.
 */
export function guideSectionIdForPath(pathname: string): string | null {
  let bestId: string | null = null;
  let bestLength = -1;

  for (const section of GUIDE_SECTIONS) {
    for (const route of section.routes ?? []) {
      const covers = pathname === route || pathname.startsWith(`${route}/`);
      if (covers && route.length > bestLength) {
        bestId = section.id;
        bestLength = route.length;
      }
    }
  }

  return bestId;
}
